import { datadogRum } from '@datadog/browser-rum';
import { getConsentKey } from './cookie-consent-helpers';

declare global {
  interface Window {
    Relyance?: {
      agent?: {
        addEventListener: (
          event: string,
          callback: (e: RelyanceConsentEvent) => void
        ) => void;
        showPreferenceCenter?: () => void;
        consentInterfaceTemplate?: Record<string, unknown>;
      };
      showPreferenceCenter?: () => void;
    };
  }
}

interface RelyanceConsentEvent {
  consentStatus: Record<string, boolean>;
}

interface RelyanceConsentPurpose {
  purposeId: string;
  name: string;
  visible: boolean;
  checked: boolean;
  alwaysActive?: boolean;
}

interface RelyanceConsentCookie {
  model?: string;
  consentPurposes?: RelyanceConsentPurpose[];
  timestamp?: number;
  consentStorageEnabled?: boolean;
  isDefaultConsent?: boolean;
}

export interface ConsentService {
  updateUserConsent: (key: string, value: boolean) => void;
}

/**
 * Mapping from our internal category names to Relyance processing activity names.
 * These should match the processing activities configured in Relyance admin console.
 */
export const CATEGORY_MAPPING = Object.freeze({
  performance: ['data analytics'],
  functional: ['functional'],
  targeting: ['marketing', 'advertising', 'targeting'],
} as const);

type CategoryKeys = keyof typeof CATEGORY_MAPPING;

const CONSENT_COOKIE_NAME = 'rly_consent';

const Relyance = {
  CATEGORY_MAPPING,
  RELYANCE_READY_EVENT_NAME: 'RELYANCE_CONSENT_UPDATE',
  RELYANCE_CONSENT_UPDATE_NAME: 'RELYANCE_CONSENT_UPDATE',
  CONSENT_COOKIE_NAME,

  /**
   * Invokes a function when Relyance is ready.
   * Relyance fires events on each consent update.
   * @param {function} fn - The function to be invoked
   */
  onRelyanceReady(fn: () => void) {
    if (window.Relyance?.agent) {
      fn();
      return;
    }
    // Wait for Relyance to load by polling
    const checkInterval = setInterval(() => {
      if (window.Relyance?.agent) {
        clearInterval(checkInterval);
        fn();
      }
    }, 100);
    // Stop polling after 10 seconds
    setTimeout(() => clearInterval(checkInterval), 10000);
  },

  /**
   * Parses the rly_consent cookie value.
   * The cookie is a base64-encoded JSON string.
   * @returns {RelyanceConsentCookie | null} - Parsed consent data or null
   */
  parseConsentCookie(): RelyanceConsentCookie | null {
    try {
      const cookieValue = document.cookie
        .split('; ')
        .find((row) => row.startsWith(`${CONSENT_COOKIE_NAME}=`))
        ?.split('=')[1];

      if (!cookieValue) {
        return null;
      }

      const cleanValue = decodeURIComponent(
        cookieValue.replace(/\+/g, '%20')
      ).replace(/\s/g, '');
      const decoded = atob(cleanValue);
      return JSON.parse(decoded) as RelyanceConsentCookie;
    } catch {
      return null;
    }
  },

  /**
   * Gets the allowed categories from the Relyance consent cookie.
   * @returns {string[]} - Array of allowed category names (lowercase)
   */
  getAllowedCategories(): string[] {
    const consentData = this.parseConsentCookie();

    if (consentData?.consentPurposes) {
      return consentData.consentPurposes
        .filter((purpose) => purpose.checked)
        .map((purpose) => (purpose.name || purpose.purposeId).toLowerCase());
    }

    return [];
  },

  /**
   * Stores the user's cookie preferences using the consent service.
   * @param {ConsentService} consentService - The consent service to update user consent.
   */
  storeCookiePreferences(consentService: ConsentService) {
    const userPreferences = this.getUserPreferences();
    Object.entries(userPreferences).forEach(([category, value]) => {
      const consentKey = getConsentKey(category);
      consentService.updateUserConsent(consentKey, value);
    });
  },

  /**
   * Gets the user's consent for a specific category.
   * @param {CategoryKeys} category - The category to get consent for.
   * @returns {boolean} - The user's consent for the category.
   */
  getConsentForCategory(category: CategoryKeys): boolean {
    const categoryNames = CATEGORY_MAPPING[category] || [];
    const allowedCategories = this.getAllowedCategories();
    return categoryNames.some((name) => allowedCategories.includes(name));
  },

  /**
   * Gets the user's preferences for all categories.
   * @returns {Record<CategoryKeys, boolean>} - The user's preferences for all categories.
   */
  getUserPreferences(): Record<CategoryKeys, boolean> {
    const allowedCategories = this.getAllowedCategories();
    const userPreferences = {} as Record<CategoryKeys, boolean>;

    for (const category in CATEGORY_MAPPING) {
      const typedCategory = category as CategoryKeys;
      const categoryNames = CATEGORY_MAPPING[typedCategory];
      userPreferences[typedCategory] = categoryNames.some((name) =>
        allowedCategories.includes(name)
      );
    }

    return userPreferences;
  },

  /**
   * Determines if the user has interacted with the cookie consent banner.
   * For Relyance, we check if the rly_consent cookie exists and is not default consent.
   * @returns {boolean} - True if consent cookie exists with user selection, false otherwise.
   */
  hasBannerInteraction(): boolean {
    const consentData = this.parseConsentCookie();
    return consentData !== null && consentData.isDefaultConsent !== true;
  },

  /**
   * Checks if consent data is available from Relyance.
   * @returns {boolean} - True if consent data is available, false otherwise.
   */
  hasConsentData(): boolean {
    return this.parseConsentCookie() !== null;
  },

  _preferenceCenterPollInterval: undefined as
    | ReturnType<typeof setInterval>
    | undefined,

  /**
   * Opens the Relyance preference center dialog.
   *
   * The Relyance SDK fetches its consent template asynchronously after the
   * script loads.  If showPreferenceCenter is called before that fetch
   * completes, the SDK silently resolves without rendering anything.  We
   * guard against this by polling for the template before invoking the SDK.
   */
  showPreferenceCenter() {
    const showFn =
      window.Relyance?.showPreferenceCenter ??
      window.Relyance?.agent?.showPreferenceCenter;

    if (!showFn) {
      const message = window.Relyance
        ? 'Relyance preference center method not available'
        : 'Relyance SDK not loaded';
      console.warn(message);
      datadogRum.addError(new Error(message), { source: 'relyance' });
      return;
    }

    if (window.Relyance!.agent?.consentInterfaceTemplate) {
      showFn.call(window.Relyance);
      return;
    }

    // Already polling from a previous click — don't create another interval
    if (this._preferenceCenterPollInterval) {
      return;
    }

    // Template not yet loaded — poll until the SDK is fully initialized
    const POLL_INTERVAL_MS = 100;
    const MAX_WAIT_MS = 5000;
    let elapsed = 0;
    this._preferenceCenterPollInterval = setInterval(() => {
      elapsed += POLL_INTERVAL_MS;
      if (window.Relyance?.agent?.consentInterfaceTemplate) {
        clearInterval(this._preferenceCenterPollInterval);
        this._preferenceCenterPollInterval = undefined;
        const fn =
          window.Relyance.showPreferenceCenter ??
          window.Relyance.agent?.showPreferenceCenter;
        fn?.call(window.Relyance);
      } else if (elapsed >= MAX_WAIT_MS) {
        clearInterval(this._preferenceCenterPollInterval);
        this._preferenceCenterPollInterval = undefined;
        const message = 'Relyance preference center not ready after timeout';
        console.warn(message);
        datadogRum.addError(new Error(message), { source: 'relyance' });
      }
    }, POLL_INTERVAL_MS);
  },
};

export default Relyance;
